import React from 'react';

/**
 * CompanyReviewsStatCard
 *
 * A single stat card for the Company Reviews dashboard header row.
 *
 * Props:
 *   label      {string}  — Card title (e.g. "28-DAY MA (URI)")
 *   value      {string}  — Primary value displayed large (e.g. "36.3")
 *   unit       {string}  — Unit below the value (e.g. "reviews/day")
 *   badge      {string}  — Pill badge text (e.g. "+16.4% vs Q1 start")
 *   sub        {string}  — Secondary subtitle line (e.g. "74 days tracked so far")
 *   progress   {number}  — Optional 0-100 progress bar value; omit to hide bar
 *   progressLabel {string} — Text shown at the right end of the progress bar
 */
const CompanyReviewsStatCard = ({
  label,
  value,
  unit,
  badge,
  sub,
  progress,
  progressLabel,
}) => {
  return (
    <div
      className="rounded p-2 h-100"
      style={{
        backgroundColor: '#111008',
        border: '1px solid #3a2e00',
        minWidth: 0,
      }}
    >
      {/* Card label with green dot */}
      <div className="d-flex align-items-center mb-1" style={{ gap: 5 }}>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#c9a84c',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: '0.65rem', color: '#9a8a60', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}
        </span>
      </div>

      {/* Primary value */}
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
        {value ?? '—'}
      </div>

      {/* Unit line */}
      {unit && (
        <div style={{ fontSize: '0.72rem', color: '#9a8a60', marginTop: 1 }}>
          {unit}
        </div>
      )}

      {/* Progress bar (optional) */}
      {typeof progress === 'number' && (
        <div className="mt-1">
          {sub && (
            <div style={{ fontSize: '0.68rem', color: '#9a8a60', marginBottom: 3 }}>
              {sub}
            </div>
          )}
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <div
              className="flex-grow-1"
              style={{
                height: 5,
                borderRadius: 3,
                backgroundColor: '#2a2000',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(progress, 100)}%`,
                  backgroundColor: '#c9a84c',
                  borderRadius: 3,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
            {progressLabel && (
              <span style={{ fontSize: '0.65rem', color: '#9a8a60', flexShrink: 0 }}>
                {progressLabel}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sub text (when no progress bar) */}
      {typeof progress !== 'number' && sub && (
        <div style={{ fontSize: '0.68rem', color: '#9a8a60', marginTop: 3 }}>
          {sub}
        </div>
      )}

      {/* Badge pill */}
      {badge && (
        <div className="mt-1">
          <span
            style={{
              display: 'inline-block',
              fontSize: '0.65rem',
              fontWeight: 600,
              color: '#c9a84c',
              backgroundColor: 'rgba(201,168,76,0.12)',
              borderRadius: 20,
              padding: '1px 7px',
            }}
          >
            {badge}
          </span>
        </div>
      )}
    </div>
  );
};

export default CompanyReviewsStatCard;
